import {
  Box, Checkbox, styled,
} from '@mui/material';
import CustomTypography from '@/components/Common/Typography';
import { corePalette } from '@/themes/colours';
import {
  CHART_LANE_COLOURS,
  CHART_LANE_CONTROL_ICON_SIZE,
  CHART_LANE_MIN_ROW_HEIGHT,
  CHART_LANE_Y_AXIS_WIDTH,
  LABEL_WIDTH,
  ROW_HEIGHT,
  TRACK_GRID_BACKGROUND,
} from '@/constants/PatientDashboard/patientTimeline';

// The rails a chart lane is drawn between. Every surface that spans a lane row carries the
// pair, so a plot, its label column and a message standing in for either line up.
export const LANE_BORDERS = {
  borderTop: `1px solid ${CHART_LANE_COLOURS.gridLine}`,
  borderBottom: `1px solid ${CHART_LANE_COLOURS.gridLine}`,
} as const;

// Shared by the two lane-sized notices below, which differ only in how tall they stand and
// what they are drawn against.
const LANE_MESSAGE_BASE = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  backgroundColor: corePalette.grey10,
} as const;

// The label column and the plot beside it are the two halves of one lane row, and either can
// be the taller — `stretch` is what lets the shorter one grow to the row instead of leaving a
// gap under itself.
export const ChartLaneRow = styled(Box)({
  display: 'flex',
  alignItems: 'stretch',
});

export const ChartLaneRoot = styled(Box)({
  position: 'relative',
  flex: 1,
  minWidth: 0,
  // Stretches to the row: the label column beside it may be the taller half.
  minHeight: `${CHART_LANE_MIN_ROW_HEIGHT}px`,
});

export const ChartLaneYAxis = styled(Box)({
  position: 'absolute',
  top: 0,
  right: '100%',
  height: '100%',
  width: `${CHART_LANE_Y_AXIS_WIDTH}px`,
  boxSizing: 'border-box',
  borderTop: '1px solid transparent',
  borderBottom: '1px solid transparent',
  pointerEvents: 'none',
});

export const ChartLaneArea = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '100%',
  ...LANE_BORDERS,
  overflow: 'hidden',
  ...TRACK_GRID_BACKGROUND,
});

export const ChartLaneMessage = styled(Box)({
  ...LANE_MESSAGE_BASE,
  minHeight: `${CHART_LANE_MIN_ROW_HEIGHT}px`,
  ...LANE_BORDERS,
  color: CHART_LANE_COLOURS.axisLabel,
});

// A notice with nothing to plot behind it needs no room for an axis, so it stands at track
// height instead of reserving a whole chart lane.
const COMPACT_LANE_MESSAGE = {
  ...LANE_MESSAGE_BASE,
  minHeight: `${ROW_HEIGHT}px`,
  padding: '8px',
  color: corePalette.grey100,
} as const;

// A track lane is a stack of row-height tracks, so its failure notice stays row-sized
// instead of reserving the height a chart lane's message does.
export const TrackLaneMessage = styled(Box)(COMPACT_LANE_MESSAGE);

// Stands in for a whole chart lane that has no series at all, so unlike the track notice it
// carries the lane rules that its plot would otherwise have drawn.
export const ChartLaneEmpty = styled(Box)({
  ...COMPACT_LANE_MESSAGE,
  ...LANE_BORDERS,
});

export const ChartLaneYAxisLabel = styled(Box)({
  position: 'absolute',
  right: '6px',
  transform: 'translateY(-50%)',
  fontWeight: 400,
  fontSize: '11px',
  lineHeight: '13px',
  color: CHART_LANE_COLOURS.axisLabel,
  whiteSpace: 'nowrap',
});

export const ChartLaneSvg = styled('svg')({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  overflow: 'visible',
  pointerEvents: 'none',
});

// The fixed-width column to the left of every lane's plot, where each lane names and controls
// its own series. What it holds differs per lane; that it is exactly as wide as the timeline's
// label gutter does not.
export const LaneLabelColumn = styled(Box)({
  width: `${LABEL_WIDTH}px`,
  flexShrink: 0,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
});

export const LaneRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});

export const LaneCheckbox = styled(Checkbox)({
  padding: 0,
  color: corePalette.grey200,
  '&.Mui-checked': {
    color: CHART_LANE_COLOURS.toggleOn,
  },
  '& .MuiSvgIcon-root': {
    fontSize: CHART_LANE_CONTROL_ICON_SIZE,
  },
});

export const LaneRowName = styled(CustomTypography)({
  flex: 1,
  minWidth: 0,
  color: corePalette.grey200,
});
